import React from 'react';

type Theme = 'green' | 'red' | 'blue' | 'purple' | 'black' | 'pink';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

const themeColors: Record<Theme, { primary: string; hover: string; rgb: string }> = {
  green: {
    primary: '#15803d',
    hover: '#166534',
    rgb: '21, 128, 61'
  },
  red: {
    primary: '#dc2626',
    hover: '#b91c1c',
    rgb: '220, 38, 38'
  },
  blue: {
    primary: '#2563eb',
    hover: '#1d4ed8',
    rgb: '37, 99, 235'
  },
  purple: {
    primary: '#9333ea',
    hover: '#7e22ce',
    rgb: '147, 51, 234'
  },
  black: {
    primary: '#3f3f46',
    hover: '#27272a',
    rgb: '63, 63, 70'
  },
  pink: {
    primary: '#ec4899',
    hover: '#db2777',
    rgb: '236, 72, 153'
  }
};

const themeOrder: Theme[] = ['green', 'red', 'blue', 'purple', 'black', 'pink'];

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = React.useState<Theme>(() => {
    const saved = localStorage.getItem('app-theme');
    return themeOrder.includes(saved as Theme) ? (saved as Theme) : 'green';
  });

  React.useEffect(() => {
    localStorage.setItem('app-theme', theme);
    
    const root = document.documentElement;
    const colors = themeColors[theme];
    root.style.setProperty('--accent-primary', colors.primary);
    root.style.setProperty('--accent-primary-hover', colors.hover);
    root.style.setProperty('--accent-primary-rgb', colors.rgb);
    
    const hueMap: Record<Theme, number> = {
      green: 86,
      red: 0,
      blue: 217,
      purple: 271,
      black: 0,
      pink: 330
    };
    root.style.setProperty('--accent-primary-hue', hueMap[theme].toString());
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => {
      const currentIndex = themeOrder.indexOf(prev);
      const nextIndex = (currentIndex + 1) % themeOrder.length;
      return themeOrder[nextIndex];
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};